import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useUsage } from "@/lib/usage";
import { GoProButton } from "@/pages/UpgradeDialog";

const SEEN_KEY_PREFIX = "snaplist.trialReminderSeen.";

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
      <p className="font-mono text-xl font-bold tabular-nums text-slate-900" data-testid={testid}>{value}</p>
      <p className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
    </div>
  );
}

export default function TrialReminderDialog() {
  const { isTrial } = useUsage();
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isTrial) return;
    const key = SEEN_KEY_PREFIX + new Date().toISOString().slice(0, 10);
    try { if (localStorage.getItem(key)) return; } catch { /* ignore */ }
    api.get("/trial/summary").then(({ data }) => {
      if (!data?.show_reminder) return;
      setSummary(data);
      setOpen(true);
      try { localStorage.setItem(key, "1"); } catch { /* ignore */ }
    }).catch(() => {});
  }, [isTrial]);

  if (!summary) return null;
  const s = summary.stats;
  const hours = Math.max(1, Math.round(summary.hours_left));
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="trial-reminder-dialog">
        <DialogHeader>
          <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-rose-100 text-rose-600">
            <Clock className="h-5 w-5" />
          </div>
          <DialogTitle className="text-2xl font-bold tracking-tight">Your Pro trial ends in {hours}h</DialogTitle>
          <DialogDescription>Here's what you've done with SnapList Pro so far.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <Stat label="Drafts created" value={s.drafts_created} testid="trial-stat-drafts" />
          <Stat label="Listings published" value={s.listings_published} testid="trial-stat-published" />
          <Stat label="Items sold" value={s.items_sold} testid="trial-stat-sold" />
          <Stat label="Sales revenue" value={`$${Number(s.revenue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} testid="trial-stat-revenue" />
        </div>
        <p className="text-xs text-slate-500">When the trial ends you'll need to pick a plan to keep using SnapList.</p>
        <GoProButton className="w-full" plan="yearly" label="Keep Pro — $329.99/yr" testid="trial-keep-pro-btn" />
        <button type="button" onClick={() => setOpen(false)} className="text-center text-xs text-slate-500 underline" data-testid="trial-reminder-later">
          Maybe later
        </button>
      </DialogContent>
    </Dialog>
  );
}
